<template>
    <media-library-renderless
        ref="mediaLibraryRenderless"
        :after-upload="afterUpload"
        :before-upload="beforeUpload"
        :initial-value="initialValue"
        :max-items="computedMaxItems"
        :max-size-for-preview-in-bytes="maxSizeForPreviewInBytes"
        :multiple="multiple"
        :name="name"
        :route-prefix="routePrefix"
        :translations="translations"
        :upload-domain="uploadDomain"
        :validation-errors="validationErrors"
        :validation-rules="validationRules"
        :vapor="vapor"
        :vapor-signed-storage-url="vaporSignedStorageUrl"
        :with-credentials="withCredentials"
        @changed="$emit('change', $event)"
        @is-ready-to-submit-change="$emit('is-ready-to-submit-change', $event)"
        @has-uploads-in-progress-change="$emit('has-uploads-in-progress-change', $event)"
    >
        <div
            slot-scope="{
                state,
                getImgProps,
                getNameInputProps,
                getNameInputListeners,
                getNameInputErrors,
                getCustomPropertyInputProps,
                getCustomPropertyInputListeners,
                getCustomPropertyInputErrors,
                getDropZoneProps,
                getDropZoneListeners,
                getFileInputProps,
                getFileInputListeners,
                removeMedia,
                replaceMedia,
                getErrors,
                clearObjectErrors,
                clearInvalidMedia,
            }"
        >
            <icons />

            <div :class="[multiple ? 'media-library-multiple' : 'media-library-single', state.media.length === 0 ? 'media-library-empty' : 'media-library-filled']" class="media-library">
                <list-errors v-if="typeof validationErrors[name] !== 'string'" :invalid-media="state.invalidMedia" :top-level-errors="validationErrors[name]" @cleared="clearInvalidMedia()" />

                <div class="media-library-items">
                    <div v-for="object in state.media" :key="object.attributes.uuid" class="media-library-item">
                        <thumb :img-props="getImgProps(object)" :uploadInfo="object.upload" :validation-rules="validationRules" @replaced="replaceMedia(object, $event)" @removed="removeMedia(object, $event)">
                            <template #errors>
                                <item-errors v-if="getErrors(object).length" :object-errors="getErrors(object)" @back="clearObjectErrors(object)" />
                            </template>
                            <template #removeMedia>
                                <div class="media-library-row-remove" dusk="remove" @click.stop="removeMedia(object)">
                                    <div>
                                        <icon icon="remove" />
                                    </div>
                                </div>
                            </template>
                        </thumb>
                    </div>
                    <div v-if="!computedMaxItems || state.media.length < computedMaxItems" class="media-library-item media-library-item-last" :class="{ 'media-library-item-hidden': !state.media.length || state.media.length === 3 }" @click="$refs.uploader.$refs.fileInputRef.click()">
                        <div class="media-library-thumb">
                            <div class="media-library-placeholder group">
                                <svg xmlns="http://www.w3.org/2000/svg" class="media-library-icon w-10 h-10 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <hidden-fields :media-state="state.media" :name="name" />

                <div v-show="!state.media.length">
                    <div v-show="!computedMaxItems || state.media.length < computedMaxItems" class="media-library-uploader">
                        <uploader
                            ref="uploader"
                            v-bind="{
                                ...getDropZoneProps(),
                                ...getFileInputProps(),
                            }"
                            v-on="{
                                ...getDropZoneListeners(),
                                ...getFileInputListeners(),
                            }"
                            :multiple="multiple"
                            add
                        />
                    </div>
                </div>
            </div>
        </div>
    </media-library-renderless>
</template>

<script>
import { DropZone, HiddenFields, Icon, Icons, MediaLibraryRenderless } from "@spatie/media-library-pro-vue2"
// import {DropZone, HiddenFields, Icons, MediaLibraryRenderless,} from '@spatie/media-library-pro-vue2';
import Uploader from "@/Media-Library/Uploader"
import ItemErrors from "@/Media-Library/ItemErrors"
import ListErrors from "@/Media-Library/ListErrors"
import Thumb from "@/Media-Library/Thumb"

export default {
    props: {
        name: { required: false, type: String },
        initialValue: { required: false, type: [Array, Object] },
        routePrefix: { required: false, type: String },
        translations: {
            default: () => {},
            type: Object,
        },
        validationRules: { required: false, type: Object },
        validationErrors: { default: () => ({}), type: [Object, Array] },
        multiple: { default: false, type: Boolean },
        maxItems: { required: false, type: Number },
        maxSizeForPreviewInBytes: { required: false, type: Number },
        vapor: { required: false, type: Boolean },
        vaporSignedStorageUrl: { required: false, type: String },
        uploadDomain: { required: false, type: String },
        withCredentials: { required: false, type: Boolean },
        beforeUpload: {
            default: () => {},
            type: Function,
        },
        afterUpload: {
            default: () => {},
            type: Function,
        },
    },

    emits: ["change", "is-ready-to-submit-change", "has-uploads-in-progress-change"],

    components: {
        MediaLibraryRenderless,
        HiddenFields,
        DropZone,
        ListErrors,
        ItemErrors,
        Icons,
        Icon,
        Thumb,
        Uploader,
    },

    data: () => ({
        showUploader: true,
        mediaLibrary: null,
        window,
    }),

    mounted() {
        this.mediaLibrary = this.$refs.mediaLibraryRenderless.mediaLibrary
        this.showUploader = !this.mediaLibrary.state.media.length
    },

    computed: {
        computedMaxItems() {
            return this.multiple ? this.maxItems : 1
        },
    },

    methods: {
        resetMediaLibraryState() {
            this.$nextTick(() => {
                if (this.$refs.mediaLibraryRenderless && this.$refs.mediaLibraryRenderless.mediaLibrary) {
                    // Directly reset the state if no provided method exists
                    this.$refs.mediaLibraryRenderless.mediaLibrary.state.media = []

                    // Ideally, call a method if available, e.g.,
                    // this.$refs.mediaLibraryRenderless.resetState();
                }
            })
        },
        checkForMediaErrorsAndReset() {
            if (this.validationErrors && this.validationErrors[this.name]) {
                this.resetMediaLibraryState()
            }
        },
        resetMediaState() {
            if (this.mediaLibrary) {
                this.mediaLibrary.state.media = []
            }
        },
    },
    watch: {
        validationErrors: {
            deep: true,
            immediate: true,
            handler() {
                this.checkForMediaErrorsAndReset()
            },
        },
    },

    created() {
        this.$on("resetMediaLibrary", this.resetMediaState)
    },
}
</script>
